import { z } from 'zod';

/** Common sync columns. `updatedAt` is an HLC stamp (see ids.ts); deletes are soft so they propagate. */
export const syncMeta = {
  updatedAt: z.string().min(13),
  deletedAt: z.string().nullable().default(null),
};

export const Mode = z.enum(['focus', 'short', 'long', 'track']);
export type Mode = z.infer<typeof Mode>;

/** `parked`: a Track run switched away from (TW-32). Resumed the same day it stays the same row; Finish completes it. */
export const SessionStatus = z.enum(['completed', 'skipped', 'break', 'parked']);
/** `agent`: added by an agent on the person's behalf (TW-36), with a token allowed to add sessions. */
export const SessionSource = z.enum(['timer', 'manual', 'split', 'agent']);
/** The named colours. The first five are the Design Book's; the rest fill the gaps round the circle. */
export const ProjectPreset = z.enum(['tomato', 'blue', 'green', 'amber', 'violet', 'lime', 'teal', 'sky', 'pink', 'slate']);
export type ProjectPreset = z.infer<typeof ProjectPreset>;

/** Anything else the user picks, as a plain lower-case hex. One colour, used in both themes. */
export const CUSTOM_COLOR = /^#[0-9a-f]{6}$/;
export const CustomColor = z.string().regex(CUSTOM_COLOR);

export type ProjectColor = ProjectPreset | `#${string}`;
export const ProjectColor: z.ZodType<ProjectColor> = z.union([ProjectPreset, CustomColor as unknown as z.ZodType<`#${string}`>]);

export const TimerStyle = z.enum(['ring', 'bar', 'digits']);
export const ThemePref = z.enum(['system', 'light', 'dark']);

export const TimeFormat = z.enum(['24h', '12h']);
export type TimeFormat = z.infer<typeof TimeFormat>;
export const DateFormat = z.enum(['dmy', 'mdy', 'ymd']);
export type DateFormat = z.infer<typeof DateFormat>;

export const Settings = z.object({
  id: z.literal('settings'),
  focusMin: z.number().int().min(1).max(90).default(25),
  shortMin: z.number().int().min(1).max(90).default(5),
  longMin: z.number().int().min(1).max(90).default(15),
  longAfter: z.union([z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).default(4),
  autoStartBreaks: z.boolean().default(true),
  sound: z.boolean().default(true),
  notifications: z.boolean().default(true),
  theme: ThemePref.default('system'),
  timerStyle: TimerStyle.default('ring'),
  tabTitleSeconds: z.boolean().default(true),
  /** How clock times and calendar dates are shown and typed (Settings › Appearance). The browser's locale is not consulted. */
  timeFormat: TimeFormat.default('24h'),
  dateFormat: DateFormat.default('dmy'),
  /** 0 = off; otherwise minutes before the first "still tracking?" nudge (then every 60m). */
  longRunReminderMin: z.union([z.literal(0), z.literal(30), z.literal(60), z.literal(90), z.literal(120)]).default(0),
  ...syncMeta,
});
export type Settings = z.infer<typeof Settings>;

export const Label = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid().nullable().default(null),
  name: z.string().min(1).max(40),
  tint: ProjectColor.nullable().default(null),
  position: z.number().int().default(0),
  retiredAt: z.string().nullable().default(null),
  ...syncMeta,
});
export type Label = z.infer<typeof Label>;

export const Project = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid().nullable().default(null),
  name: z.string().min(1).max(80),
  color: ProjectColor,
  labelIds: z.array(z.string().uuid()).default([]),
  weeklyTargetSec: z.number().int().positive().nullable().default(null),
  position: z.number().int().default(0),
  archivedAt: z.string().nullable().default(null),
  ...syncMeta,
});
export type Project = z.infer<typeof Project>;

/**
 * What a task says about itself, kept together because nothing sorts or filters on it: one JSON column in D1, sent
 * whole through sync and the team routes. A new field is a line here plus its UI - no migration, no route change.
 * Give it a default, so rows written before it existed read as complete. Anything the server has to query (an
 * index, a filter, a sort) belongs on `Task` as a column instead.
 */
export const TaskDetails = z.object({
  /** What the task is about, in plain text. Links in it are clickable. */
  notes: z.string().max(5000).default(''),
  /** Where the task came from, when an integration made it (TW-19): shown as "TW-12" with a link to the ticket. */
  source: z.object({ kind: z.literal('plane'), identifier: z.string().max(40), url: z.string().url().max(500) }).nullable().default(null),
});
export type TaskDetails = z.infer<typeof TaskDetails>;
/**
 * A task's details with every default filled in. Rows reach the UI from Dexie, sync and the team API without being
 * parsed, and ones older than a field lack it, so read details through this rather than `task.details` directly.
 */
export const readTaskDetails = (t: { details?: unknown }): TaskDetails => {
  const r = TaskDetails.safeParse(t.details ?? {});
  return r.success ? r.data : TaskDetails.parse({});
};

export const Task = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string().min(1).max(200),
  /** Duration estimate in seconds; null = no estimate. Estimates are durations, never pomodoro counts. */
  estimateSec: z.number().int().positive().nullable().default(null),
  /** Cache of the task's session total; recomputed from sessions. */
  trackedSec: z.number().int().nonnegative().default(0),
  labelIds: z.array(z.string().uuid()).default([]),
  finishedAt: z.string().nullable().default(null),
  position: z.number().int().default(0),
  /** Written only by the server (team plane). */
  claimedBy: z.string().uuid().nullable().default(null),
  claimedAt: z.string().nullable().default(null),
  // `prefault`, not `default`: Zod 4 returns a default as it is, so `{}` would skip the defaults inside it.
  details: TaskDetails.prefault({}),
  ...syncMeta,
});
export type Task = z.infer<typeof Task>;

export const Session = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid().nullable().default(null),
  taskId: z.string().uuid().nullable().default(null),
  projectId: z.string().uuid().nullable().default(null),
  mode: Mode,
  status: SessionStatus,
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
  durationSec: z.number().int().nonnegative(),
  goalText: z.string().max(200).default(''),
  labelIds: z.array(z.string().uuid()).default([]),
  source: SessionSource.default('timer'),
  editedAt: z.string().nullable().default(null),
  editedBy: z.string().uuid().nullable().default(null),
  ...syncMeta,
});
export type Session = z.infer<typeof Session>;

export const TimerStatus = z.enum(['idle', 'running', 'paused']);
export const ActiveTimer = z.object({
  id: z.literal('active'),
  runId: z.string().uuid().nullable().default(null),
  status: TimerStatus.default('idle'),
  mode: Mode.default('focus'),
  taskId: z.string().uuid().nullable().default(null),
  projectId: z.string().uuid().nullable().default(null),
  goalText: z.string().default(''),
  /**
   * The labels this session will be logged with, when set from the Timer's goal card (TW-6). Null follows the goal:
   * the task's plus the project's, read when the session is logged. Cleared once a session is logged or the goal changes,
   * so a label added here is for this session only and never lands on the task.
   */
  labelIds: z.array(z.string().uuid()).nullable().default(null),
  startedAt: z.string().datetime().nullable().default(null),
  /** Countdown target in seconds; null in track mode. */
  durationSec: z.number().int().positive().nullable().default(null),
  /** Elapsed seconds banked before the current running stretch. */
  accumSec: z.number().nonnegative().default(0),
  /** Start of the current running stretch; null while paused/idle. */
  anchorAt: z.string().datetime().nullable().default(null),
  /** Track mode: stretches with no user input, for "Split at idle". */
  idleSegments: z.array(z.tuple([z.string().datetime(), z.string().datetime()])).default([]),
  /** Focus sessions completed in the current pomodoro cycle; resets after the long break. Drives "Session 2 of 4". */
  cycleCount: z.number().int().nonnegative().default(0),
  startedOnDeviceId: z.string().nullable().default(null),
  ...syncMeta,
});
export type ActiveTimer = z.infer<typeof ActiveTimer>;


/**
 * One stretch of work an AI agent did on a task (architecture §11.3). Deliberately *not* a session: agent time is
 * never the person's time, so it never enters totals, targets or the day log. An open run - `endedAt`
 * null - is "in flight". Tokens and cost arrive from the agent's host and are often missing; every view has to
 * read properly without them.
 */
export const AgentRun = z.object({
  id: z.string().uuid(),
  /** Set when the run is on a shared project; those rows go through /v1/teams, like every other team row. */
  teamId: z.string().uuid().nullable().default(null),
  taskId: z.string().uuid().nullable().default(null),
  projectId: z.string().uuid().nullable().default(null),
  /** Free text as the agent reports itself, e.g. "Claude Code". Named, never labelled just "AI". */
  agent: z.string().min(1).max(60),
  model: z.string().max(60).nullable().default(null),
  startedAt: z.string().datetime(),
  /** Null while in flight. */
  endedAt: z.string().datetime().nullable().default(null),
  tokensIn: z.number().int().nonnegative().nullable().default(null),
  tokensOut: z.number().int().nonnegative().nullable().default(null),
  /** Whole cents; null until the agent's host reports usage. */
  costCents: z.number().int().nonnegative().nullable().default(null),
  note: z.string().max(280).nullable().default(null),
  ...syncMeta,
});
export type AgentRun = z.infer<typeof AgentRun>;

export const SyncTable = z.enum(['settings', 'labels', 'projects', 'tasks', 'sessions', 'activeTimer', 'agentRuns']);
export type SyncTable = z.infer<typeof SyncTable>;
export const rowSchemas = {
  settings: Settings, labels: Label, projects: Project, tasks: Task, sessions: Session, activeTimer: ActiveTimer, agentRuns: AgentRun,
} as const;
